import type { GameView } from '../types';
import { AppScreen } from './AppScreen';
import { MedalIcon, ShareIcon } from './icons';
import { shareGameCode } from '../lib/share';
import { useToast } from '../context/ToastContext';

interface ResultsScreenProps {
  game: GameView | null;
  onPlayAgain: () => void;
  onNewGame: () => void;
}

function ordinal(n: number): string {
  const v = n % 100;
  if (v >= 11 && v <= 13) return `${n}th`;
  switch (n % 10) {
    case 1: return `${n}st`;
    case 2: return `${n}nd`;
    case 3: return `${n}rd`;
    default: return `${n}th`;
  }
}

export function ResultsScreen({ game, onPlayAgain, onNewGame }: ResultsScreenProps) {
  const { showToast } = useToast();
  const sorted = [...(game?.players ?? [])].sort((a, b) => b.score - a.score);

  let winnerText = '';
  if (game?.isSinglePlayer && sorted[0]) {
    winnerText = `${sorted[0].name} — ${sorted[0].score} / 26 points`;
  } else if (sorted.length > 0) {
    const top = sorted[0];
    const tied = sorted.filter((p) => p.score === top.score);
    winnerText = tied.length > 1 ? `Tie! ${tied.map((p) => p.name).join(' & ')}` : `Winner: ${top.name}`;
  }

  async function handleShare() {
    const code = game?.code;
    if (code) {
      await shareGameCode(code, showToast);
    } else {
      const text = `I just played Alphabet Hunter — ${winnerText}`;
      if (navigator.share) {
        try {
          await navigator.share({ title: 'Alphabet Hunter', text });
        } catch {
          /* cancelled */
        }
      } else {
        await navigator.clipboard.writeText(text);
        showToast('Copied results to clipboard!');
      }
    }
  }

  return (
    <AppScreen>
      <div className="sticky top-0 z-10 bg-atlas-bg dark:bg-atlas-bg-dark shrink-0 flex items-center justify-center pt-[calc(env(safe-area-inset-top)+14px)] pb-1.5 px-6">
        <span className="text-xs font-semibold uppercase tracking-wide text-atlas-ink-soft dark:text-atlas-ink-soft-dark">
          Game Complete
        </span>
      </div>

      <main className="flex-1 px-6 pt-2 flex flex-col gap-5">
        <div className="flex flex-col items-center gap-2 pt-3">
          <MedalIcon className="w-[46px] h-[46px] text-atlas-brass dark:text-atlas-brass-dark" />
          <span className="font-serif text-2xl font-bold text-atlas-ink dark:text-atlas-ink-dark text-center">
            {winnerText}
          </span>
        </div>

        <div className="flex flex-col gap-2">
          <span className="text-xs font-medium uppercase tracking-wide text-atlas-ink-soft dark:text-atlas-ink-soft-dark">
            Final Standings
          </span>
          {sorted.map((p, i) => (
            <div
              key={`${p.name}-${i}`}
              className="flex items-center gap-3 bg-atlas-surface dark:bg-atlas-surface-dark rounded-xl px-3.5 py-3"
            >
              <div
                className={`w-[26px] h-[26px] rounded-full flex items-center justify-center text-xs font-bold font-mono shrink-0 ${
                  i === 0
                    ? 'bg-atlas-brass dark:bg-atlas-brass-dark text-atlas-ink'
                    : 'border border-atlas-rule dark:border-atlas-rule-dark text-atlas-ink-soft dark:text-atlas-ink-soft-dark'
                }`}
              >
                {ordinal(i + 1).replace(/\D+$/, '')}
              </div>
              <span className="flex-1 text-[15px] font-semibold text-atlas-ink dark:text-atlas-ink-dark truncate">
                {p.name}
              </span>
              <span className="font-mono text-base font-semibold text-atlas-ink dark:text-atlas-ink-dark">
                {p.score}
              </span>
            </div>
          ))}
        </div>
      </main>

      <div className="sticky bottom-0 z-10 bg-atlas-bg dark:bg-atlas-bg-dark shrink-0 px-6 pt-2 pb-2 flex flex-col gap-2.5">
        <button
          onClick={onPlayAgain}
          className="w-full h-[52px] rounded-xl bg-atlas-brass dark:bg-atlas-brass-dark hover:brightness-95 active:brightness-90 text-atlas-ink font-semibold text-[17px] transition-all"
        >
          Play Again
        </button>
        <button
          onClick={handleShare}
          className="w-full h-11 rounded-xl border-[1.5px] border-atlas-ink dark:border-atlas-ink-dark text-atlas-ink dark:text-atlas-ink-dark font-semibold text-[15px] flex items-center justify-center gap-2 hover:bg-atlas-surface dark:hover:bg-atlas-surface-dark transition-colors"
        >
          <ShareIcon className="w-4 h-4" />
          Share Results
        </button>
        <button
          onClick={onNewGame}
          className="text-sm font-medium text-atlas-ink-soft dark:text-atlas-ink-soft-dark hover:text-atlas-ink dark:hover:text-atlas-ink-dark text-center"
        >
          New Game
        </button>
      </div>
    </AppScreen>
  );
}
